/**
 * Wire schemas for module 3: quality (checklist templates, inspection requests,
 * inspections and their item results, NCRs, rectifications, floor plans, snags,
 * tests) and safety (incidents, toolbox talks and attendance, PPE, summaries).
 *
 * Synced entities are parsed here before they reach IndexedDB. Missing optional
 * fields get safe defaults so a newer server cannot break an older client.
 */
import { z } from 'zod';

const id = z.string();
const nullableId = z.string().nullable().default(null);
const date = z.string().nullable().default(null);
const text = z.string().default('');
const version = z.number().int().nullable().default(null);
const count = z.coerce.number().catch(0).default(0);

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;
export const QUALITY_CATEGORIES = ['quality', 'safety'] as const;

export const partyRefSchema = z.object({ id, name: text, role: text });
export type PartyRef = z.infer<typeof partyRefSchema>;
const party = partyRefSchema.nullable().default(null);

// ---------------------------------------------------------------------------
// Checklist templates
// ---------------------------------------------------------------------------

export const ITEM_TYPES = ['check', 'measurement', 'photo'] as const;
export type ItemType = (typeof ITEM_TYPES)[number];

export const checklistItemSchema = z.object({
  id,
  question: z.string(),
  type: z.enum(ITEM_TYPES).catch('check'),
  required: z.boolean().default(true),
  unit: text,
  guidance: text,
});
export type ChecklistItemSpec = z.infer<typeof checklistItemSchema>;

export const templateSchema = z.object({
  id,
  /** Templates are company records; synced per project for offline walks. */
  project_id: z.string().default(''),
  organization_id: nullableId,
  built_in: z.boolean().default(false),
  key: text,
  name: z.string(),
  category: z.enum(QUALITY_CATEGORIES).catch('quality'),
  trade: text,
  stage_hint: text,
  description: text,
  items: z.array(checklistItemSchema).catch([]).default([]),
  is_active: z.boolean().default(true),
  version,
});
export type ChecklistTemplate = z.infer<typeof templateSchema>;

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

export const REQUEST_STATUSES = [
  'requested',
  'scheduled',
  'in_progress',
  'passed',
  'failed',
  'cancelled',
] as const;

export const inspectionRequestSchema = z.object({
  id,
  project_id: id,
  reference: text,
  title: z.string(),
  category: z.enum(QUALITY_CATEGORIES).catch('quality'),
  template_id: nullableId,
  stage_id: nullableId,
  zone: text,
  location: text,
  requested_by_id: nullableId,
  requested_by_name: text,
  requested_by_party: party,
  requested_for: date,
  inspector_party: party,
  inspector_id: nullableId,
  inspector_name: text,
  status: z.string().default('requested'),
  notes: text,
  status_notes: text,
  client_visible: z.boolean().default(false),
  created_at: date,
  version,
});
export type InspectionRequest = z.infer<typeof inspectionRequestSchema>;

export const INSPECTION_RESULTS = ['pass', 'fail', 'conditional'] as const;
export type InspectionOutcome = (typeof INSPECTION_RESULTS)[number];

export const inspectionSchema = z.object({
  id,
  project_id: id,
  reference: text,
  request_id: nullableId,
  template_id: nullableId,
  category: z.enum(QUALITY_CATEGORIES).catch('quality'),
  title: z.string(),
  stage_id: nullableId,
  zone: text,
  /** Module 4: the zone from the project's register (``zone`` stays the label). */
  site_zone_id: nullableId,
  location: text,
  items: z.array(checklistItemSchema).catch([]).default([]),
  inspector_id: nullableId,
  inspector_name: text,
  inspector_party: party,
  started_at: date,
  /** in_progress → submitted → signed_off */
  status: z.string().default('in_progress'),
  result: z.string().nullable().default(null),
  summary: text,
  signature_asset_id: nullableId,
  submitted_at: date,
  raise_ncrs: z.boolean().default(true),
  signed_off_by_id: nullableId,
  signed_off_by_name: text,
  signed_off_at: date,
  sign_off_notes: text,
  client_visible: z.boolean().default(false),
  created_at: date,
  version,
});
export type Inspection = z.infer<typeof inspectionSchema>;
export const INSPECTION_EDITABLE = [
  'title',
  'zone',
  'site_zone_id',
  'location',
  'summary',
] as const;

export const RESULT_OUTCOMES = ['pass', 'fail', 'na'] as const;
export type ResultOutcome = (typeof RESULT_OUTCOMES)[number];

export const inspectionResultSchema = z.object({
  id,
  project_id: id,
  inspection_id: id,
  item_id: z.string(),
  outcome: z.enum(RESULT_OUTCOMES).catch('na'),
  value: text,
  notes: text,
  recorded_by_id: nullableId,
  recorded_at: z.string(),
  version,
});
export type InspectionResult = z.infer<typeof inspectionResultSchema>;

// ---------------------------------------------------------------------------
// NCRs and rectifications
// ---------------------------------------------------------------------------

export const NCR_STATUSES = [
  'raised',
  'acknowledged',
  'rectification_submitted',
  'rejected',
  'verified',
  'closed',
] as const;

export const ncrSchema = z.object({
  id,
  project_id: id,
  reference: text,
  title: z.string(),
  description: text,
  location: text,
  zone: text,
  stage_id: nullableId,
  severity: z.string().default('medium'),
  root_cause: text,
  required_action: text,
  responsible_party_id: nullableId,
  responsible_party: party,
  assignee_id: nullableId,
  due_date: date,
  status: z.string().default('raised'),
  raised_by_id: nullableId,
  raised_by_name: text,
  raised_by_party: party,
  inspection_id: nullableId,
  inspection_item_id: text,
  acknowledged_at: date,
  verified_by_id: nullableId,
  verified_at: date,
  closed_at: date,
  status_notes: text,
  client_visible: z.boolean().default(false),
  created_at: date,
  version,
});
export type Ncr = z.infer<typeof ncrSchema>;
export const NCR_EDITABLE = [
  'title',
  'description',
  'location',
  'zone',
  'stage_id',
  'severity',
  'root_cause',
  'required_action',
  'responsible_party_id',
  'assignee_id',
  'due_date',
  'client_visible',
] as const;

export const rectificationSchema = z.object({
  id,
  project_id: id,
  ncr_id: nullableId,
  snag_id: nullableId,
  notes: z.string(),
  submitted_by_id: nullableId,
  submitted_by_name: text,
  submitted_by_party: party,
  submitted_at: z.string(),
  status: z.string().default('submitted'),
  decided_by_id: nullableId,
  decided_at: date,
  decision_notes: text,
  version,
});
export type Rectification = z.infer<typeof rectificationSchema>;

// ---------------------------------------------------------------------------
// Floor plans and snags
// ---------------------------------------------------------------------------

export const floorPlanSchema = z.object({
  id,
  project_id: id,
  name: z.string(),
  level: text,
  document_id: nullableId,
  image_asset_id: nullableId,
  image_status: z.string().nullable().default(null),
  client_visible: z.boolean().default(true),
  version,
});
export type FloorPlan = z.infer<typeof floorPlanSchema>;

export const SNAG_STATUSES = ['open', 'fixed', 'verified', 'closed'] as const;

const pin = z.coerce.number().min(0).max(100).nullable().catch(null).default(null);

export const snagSchema = z.object({
  id,
  project_id: id,
  reference: text,
  title: z.string(),
  description: text,
  location: text,
  zone: text,
  site_zone_id: nullableId,
  plan_id: nullableId,
  pin_x: pin,
  pin_y: pin,
  severity: z.string().default('medium'),
  assignee_party_id: nullableId,
  assignee_party: party,
  assignee_id: nullableId,
  due_date: date,
  status: z.string().default('open'),
  raised_by_id: nullableId,
  raised_by_name: text,
  raised_by_party: party,
  fixed_by_id: nullableId,
  fixed_at: date,
  verified_at: date,
  closed_at: date,
  status_notes: text,
  client_visible: z.boolean().default(true),
  created_at: date,
  version,
});
export type Snag = z.infer<typeof snagSchema>;
export const SNAG_EDITABLE = [
  'title',
  'description',
  'location',
  'zone',
  'site_zone_id',
  'plan_id',
  'pin_x',
  'pin_y',
  'severity',
  'assignee_party_id',
  'assignee_id',
  'due_date',
  'client_visible',
] as const;

// ---------------------------------------------------------------------------
// Tests (online)
// ---------------------------------------------------------------------------

export const TEST_TYPES = [
  'pressure',
  'continuity',
  'insulation',
  'earth_loop',
  'cube_strength',
  'slump',
  'commissioning',
  'functional',
  'other',
] as const;
export const TEST_RESULTS = ['pending', 'pass', 'fail'] as const;

export const testValueSchema = z.object({
  label: text,
  value: text,
  unit: text,
  expected: text,
});
export type TestValue = z.infer<typeof testValueSchema>;

export const testRecordSchema = z.object({
  id,
  project_id: id,
  reference: text,
  title: z.string(),
  test_type: z.string().default('other'),
  stage_id: nullableId,
  location: text,
  test_date: date,
  values: z.array(testValueSchema).catch([]).default([]),
  result: z.string().default('pending'),
  status: z.string().default('planned'),
  witness_party: party,
  witnessed_by_id: nullableId,
  witnessed_by_name: text,
  witnessed_at: date,
  witness_notes: text,
  recorded_by_id: nullableId,
  recorded_by_name: text,
  recorded_by_party: party,
  certificate: z.object({ id, title: text, reference: text }).nullable().catch(null).default(null),
  notes: text,
  client_visible: z.boolean().default(true),
  created_at: date,
  version,
});
export type TestRecord = z.infer<typeof testRecordSchema>;

// ---------------------------------------------------------------------------
// Online reads: people, summary
// ---------------------------------------------------------------------------

export const qualityPersonSchema = z.object({
  id,
  user_id: text,
  name: text,
  role: text,
  can_inspect: z.boolean().default(false),
  can_rectify: z.boolean().default(false),
  can_manage_safety: z.boolean().default(false),
});
export const qualityPartySchema = partyRefSchema.extend({
  members: z.array(qualityPersonSchema).catch([]).default([]),
});
export type QualityParty = z.infer<typeof qualityPartySchema>;

export const qualitySummarySchema = z.object({
  inspections: z
    .object({
      total: count,
      passed: count,
      conditional: count,
      failed: count,
      awaiting_sign_off: count,
      in_progress: count,
      requests_open: count,
    })
    .prefault({}),
  ncrs: z
    .object({ open: count, awaiting_verification: count, overdue: count, closed: count })
    .prefault({}),
  snags: z.object({ open: count, fixed: count, verified: count }).prefault({}),
  recent: z.array(inspectionSchema).catch([]).default([]),
});
export type QualitySummary = z.infer<typeof qualitySummarySchema>;

// ---------------------------------------------------------------------------
// Safety
// ---------------------------------------------------------------------------

export const INCIDENT_KINDS = [
  'incident',
  'near_miss',
  'injury',
  'property_damage',
  'environmental',
] as const;
export const INCIDENT_STATUSES = ['reported', 'investigating', 'closed'] as const;

export const incidentActionSchema = z.object({
  id,
  description: z.string(),
  owner_id: nullableId,
  owner_name: text,
  due_date: date,
  status: z.string().default('open'),
  done_at: date,
});
export type IncidentAction = z.infer<typeof incidentActionSchema>;

export const incidentSchema = z.object({
  id,
  project_id: id,
  reference: text,
  kind: z.string().default('incident'),
  occurred_at: z.string(),
  location: text,
  zone: text,
  people_involved: text,
  description: text,
  immediate_actions: text,
  severity: z.string().default('medium'),
  lost_time: z.boolean().default(false),
  reported_by_id: nullableId,
  reported_by_name: text,
  reported_by_party: party,
  status: z.string().default('reported'),
  investigation: text,
  root_cause: text,
  closed_by_id: nullableId,
  closed_at: date,
  closing_notes: text,
  actions: z.array(incidentActionSchema).catch([]).default([]),
  created_at: date,
  version,
});
export type Incident = z.infer<typeof incidentSchema>;
export const INCIDENT_EDITABLE = [
  'kind',
  'occurred_at',
  'location',
  'zone',
  'people_involved',
  'description',
  'immediate_actions',
  'severity',
  'lost_time',
] as const;

export const toolboxTalkSchema = z.object({
  id,
  project_id: id,
  reference: text,
  topic: z.string(),
  held_on: z.string(),
  presenter_id: nullableId,
  presenter_name: text,
  duration_minutes: z.number().int().nullable().catch(null).default(null),
  notes: text,
  status: z.string().default('held'),
  party_id: nullableId,
  created_at: date,
  version,
});
export type ToolboxTalk = z.infer<typeof toolboxTalkSchema>;
export const TOOLBOX_EDITABLE = [
  'topic',
  'held_on',
  'presenter_name',
  'duration_minutes',
  'notes',
  'status',
] as const;

export const toolboxAttendanceSchema = z.object({
  id,
  project_id: id,
  talk_id: id,
  worker_id: id,
  present: z.boolean().default(true),
  recorded_by_id: nullableId,
  recorded_at: z.string(),
  version,
});
export type ToolboxAttendance = z.infer<typeof toolboxAttendanceSchema>;

export const PPE_ITEMS = [
  'helmet',
  'boots',
  'vest',
  'gloves',
  'goggles',
  'harness',
  'ear_protection',
  'mask',
  'overalls',
  'other',
] as const;

export const ppeIssueSchema = z.object({
  id,
  project_id: id,
  worker_id: id,
  worker_name: text,
  worker_trade: text,
  item: z.string(),
  description: text,
  size: text,
  quantity: z.number().int().default(1),
  issued_on: z.string(),
  returned_on: date,
  condition: text,
  notes: text,
  issued_by_name: text,
  /** The worker signed for it. */
  acknowledged: z.boolean().catch(false).default(false),
  version,
});
export type PpeIssue = z.infer<typeof ppeIssueSchema>;

/** Who PPE can be issued to (`/safety/ppe/workers`): name and trade only, never a phone. */
export const ppeWorkerSchema = z.object({
  id: z.string(),
  name: z.string(),
  trade: z.string().catch('').default(''),
  party_id: z.string().catch('').default(''),
  company: z.string().catch('').default(''),
});
export type PpeWorker = z.infer<typeof ppeWorkerSchema>;

export const safetySummarySchema = z.object({
  incidents: z
    .object({
      total: count,
      open: count,
      by_kind: z.record(z.string(), z.number()).catch({}).default({}),
      by_severity: z.record(z.string(), z.number()).catch({}).default({}),
      lost_time: count,
    })
    .prefault({}),
  days_without_lost_time: z.number().nullable().catch(null).default(null),
  trend: z
    .array(z.object({ month: z.string(), incidents: count, near_misses: count }))
    .catch([])
    .default([]),
  toolbox: z.object({ held: count, this_week: count, attendances: count }).prefault({}),
  safety_walks: z.object({ total: count, passed: count, failed: count }).prefault({}),
  open_actions: count,
  overdue_actions: count,
});
export type SafetySummary = z.infer<typeof safetySummarySchema>;
